import { useEffect, useMemo, useState } from 'react';
import { Filter } from '../../../../models/filter/filter';
import { parseRawToZips } from '../../../../services/parsing/parseRaw';
import { validateZip } from '../../../../services/validation/zip';
import { Button } from '../../../shared/inputs/button';
import { ReactComponent as ErrorIcon } from './../../../../assets/icons/error.svg';
import { ReactComponent as DeleteIcon } from './../../../../assets/icons/delete.svg';
import { ReactComponent as DropIcon } from './../../../../assets/icons/drop-file.svg';
import { ReactComponent as DontDropIcon } from './../../../../assets/icons/dont-drop-file.svg';
import { Portal } from '../../../shared/portal/portal';
import { Api } from '../../../../services/api';
import { useNavigate } from 'react-router-dom';
import { Load } from '../../../shared/load/load';
import './import.css';
import { Switch } from '../../../shared/inputs/switch';
import { ScheduleMini } from '../../../shared/inputs/schedule/scheduleMini';
import React from 'react';
import { Grid } from '../../../shared/grid/grid';
import { HeadingCard } from '../../../shared/cards/headingCard';
import { Notify } from '../../../../services/notification/notify';
import { useParams } from '../../../../services/routing/useRouteInfo';

export const ZipImport = () => {

    let { id } = useParams();
    const navigate = useNavigate();

    const [rawText, setRawText] = useState(null);
    const [data, setData] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [dataHasError, setDataHasError] = useState(false);
    const [showDropMenu, setShowDropMenu] = useState(false);
    const [validFile, setValidFile] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const selectedState = useState([]);
    const filterState = useState(new Filter());

    const onPaste = (e) => {

        if (rawText) return;

        const clipboardData = e.clipboardData || window.clipboardData;
        const pastedData = clipboardData.getData('Text');

        setRawText(pastedData.replace(/\t/g, ','));
    }

    const onDrag = (e) => {
        e.preventDefault();
        if (e.type === 'dragenter' && e.relatedTarget === null) {
            setShowDropMenu(true);

            setValidFile(e.dataTransfer.items?.[0]?.type === 'text/csv')

        } if (e.type === 'dragleave' && e.relatedTarget === null) {
            setShowDropMenu(false);
        }
    }

    const onDrop = (e) => {
        e.preventDefault();
        setShowDropMenu(false);

        if (e.dataTransfer.items?.[0]?.type === 'text/csv')
            processFile(e.dataTransfer.files[0]);
    }

    const processFile = (file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            setRawText(event.target.result);
        };
        reader.readAsText(file);
    }

    const prompt = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv'
        input.onchange = e => {
            processFile(e.target.files[0]);
        }
         
        input.click();
    }

    useEffect(() => {
        setTableData(toTableData(data));
    }, [data])

    const toTableData = (d) => {
        let foundError = false;
        let td = (d ?? []).map((r, i) => {
            const errors = validateZip(r);
            if (errors.zip || errors.county) foundError = true;
            return ({
                id: r.key,
                zip: <div className='cell'>
                    {errors.zip ? <ErrorIcon title={errors.zip} /> : ''}
                    
                    <input className='text' value={r.zip ?? ''} onChange={e => {
                        data[i].zip = e.target.value;
                        setData([...data]);
                    }} />
                
                </div>,
                schedule: <div className='cell'>
                    {errors.schedule ? <ErrorIcon title={errors.schedule} /> : ''}

                    <ScheduleMini className='shadow' initialValue={r.schedule} onChange={v =>  {
                        data[i].schedule = v;
                        setData([...data]);
                    }} />

                </div>,
                county: <div className='cell'>
                    {errors.county ? <ErrorIcon title={errors.county} /> : ''}

                    <input className='text' value={r.county ?? ''} onChange={e => {
                        data[i].county = e.target.value;
                        setData([...data]);
                    }} />
                </div>,
                acceptingNewPatients: <Switch
                    initialValue={!!r.acceptingNewPatients}
                    data={[
                        {
                            label: 'Yes',
                            value: true
                        },
                        {
                            label: 'No',
                            value: false
                        }
                    ]}
                    onChange={v => {
                        data[i].acceptingNewPatients = v;
                        setData([...data]);
                    }}
                />,
                delete: <Button className='icon' onClick={() => {
                        data.splice(i, 1);
                        setData([...data]);
                    }} >
                        <DeleteIcon title='Remove Row'/>
                    </Button>
            })
        });

        setDataHasError(foundError);

        return td;
    }

   

    
    useEffect(() => {
        window.addEventListener('paste', onPaste);
        document.addEventListener('dragenter', onDrag);
        document.addEventListener('dragleave', onDrag);
        document.addEventListener('dragover', onDrag);
        document.addEventListener('drop', onDrop);

        return () => {
            window.removeEventListener('paste', onPaste);
            document.addEventListener('dragenter', onDrag);
            document.addEventListener('dragleave', onDrag);
            document.addEventListener('dragover', onDrag);
            document.addEventListener('drop', onDrop);
        }
    }, []);


    useEffect(() => {
        if (!rawText) return

        let parsedRaw = null;

        try {
            parsedRaw = parseRawToZips(rawText, {});
        } catch {
            setRawText(null);
        }

        setData(parsedRaw);

    }, [rawText]);


    const importData = () => {
        setLoading(true);
        Api.post('/locations/zipcodes', data.map(r => ({
            zip: r.zip,
            county: r.county,
            acceptingNewPatients: !!r.acceptingNewPatients
        })))
        .then(async res => {
            await Api.post('/locations/schedules', data.map((r, i) => ({
                zipcodeId: res.content[i].id,
                branchId: id,
                ...r.schedule
            })));

            setLoading(false);
            navigate('/locations/branches/' + id)
        })
        .catch(e => {
            setLoading(false);
            console.error(e);
            Notify.error('Error Saving', 'There was an error when importing the zip codes.');
        })
    }


    const tableColumns = useMemo(() => ({
        zip: {
            label: 'Zip Code',
            sortable: false,
        },
        schedule: {
            label: 'Schedule',
            sortable: false
        },
        county: {
            label: 'County',
            sortable: false,
        },
        acceptingNewPatients: {
            label: 'Accepting New Patients',
            sortable: false
        },
        delete: {
            label: '',
            sortable: false,
            size: '100px'
        }
    }));


    return (<div className='import-wrapper'>
        {
            (loading) ?
            <Load message='Importing Zip Codes' />
            :
            <>
                <Portal>
                    <div className={'import-drop' + (showDropMenu ? ' active' : '') + (!validFile ? ' invalid' : '')}>
                        <main>
                            {validFile ? <DropIcon /> : <DontDropIcon />}
                            <h1>{validFile ? 'Drop CSV File' : 'File isn\'t a CSV'}</h1>
                            
                        </main>
                    </div>
                </Portal>

                <div className='import-view'>
                    <HeadingCard subtitle='Import Zip Codes'>Import Zip Codes</HeadingCard>

                    {
                        (!rawText) ?
                        <div className='split'>
                            <div>
                                <h1>Import Options</h1>
                                <h2>From your clipboard</h2>
                                <p>Click anywhere in the page and then paste your clipboard with <code>Ctrl + V</code></p>
                                <h2>Drop a .csv file</h2>
                                <p>Drop a .csv file anywhere in this page.</p>
                                <h2>Open a .csv file</h2>
                                <p>Select the .csv file below.</p>
                                <Button className='shadow' onClick={prompt}>Open</Button>
                            </div>
                            <div>
                                <h1>Import Format</h1>
                                <h3>Columns</h3>
                                <ol className='columns'>
                                    <li>
                                        Zip code
                                        <ul>
                                            <li>The 5 character long zip code</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Schedule
                                        <ul>
                                            <li>
                                                Any occurrence of M, T, W, TH, F will enable their respective days.<br />
                                                Ranges are possible using a dash (W - F).<br />
                                                A will import all days.
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Name
                                        <ul>
                                            <li>This value will be recapitalized (CHICAGO becomes Chicago)</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Accepting new patients 
                                        <ul>
                                            <li>Accepts either Yes or No</li>
                                        </ul>
                                    </li>
                                </ol>
                                <h2>Example</h2>
                                <Grid
                                    columns={tableColumns}
                                    data={[
                                        {id: 'demo1', zip: '10001', schedule: 'W TH', county: 'New York City', acceptingNewPatients: 'Yes'},
                                        {id: 'demo2', zip: '60007', schedule: 'M - F', county: 'Chicago', acceptingNewPatients: 'No'}
                                    ]}
                                />
                            </div>
                        </div>
                        :
                        <>
                            <header className='import-header'>
                                <Button className='shadow' type='button' title='Clear the current import and return' onClick={() => setRawText(null)}>Reset</Button>
                                <Button className='shadow primary' title='Add a new row' onClick={() => {setData([{}, ...data])}}>Add New Row</Button>
                                <h4>Importing {data?.length ?? '?'} zip codes.</h4>
                            </header>
                            <Grid
                                data={tableData}
                                columns={tableColumns}
                            />

                            <br /> <br />

                            {!data ? 'Data isn\'t loaded yet' : dataHasError ? 'There are errors in the data above. Fix those before importing.' : data?.length === 0 ? 'There are no zip codes to import.' : 'The data above is valid.'}<br /><br />
                            <button className='shadow primary' type='button' style={{width: '100%'}} disabled={!data || dataHasError || data?.length === 0} onClick={importData}>Import Zip Codes</button>
                        
                        </>
                    }
                    
                </div>
            </>
        }
    </div>)
}